import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { Button } from "@shared/components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@shared/components/ui/carousel";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@shared/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@shared/components/ui/tabs";
import type { MarketingFileView, MarketingItemView } from "@shared/lib/marketing-shared";
import { cn } from "@shared/lib/utils";

function FileFooter({ file }: { file: MarketingFileView }) {
  const facts = [file.format, file.dimensions, file.detail, file.size].filter(Boolean).join(" · ");
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 text-[12.5px] text-muted-foreground">
      <span>
        <span className="font-medium text-foreground">{file.label}</span> · {facts}
      </span>
      <Button asChild className="max-sm:h-11" size="sm" variant="outline">
        <a download href={file.downloadUrl}>
          <Download /> Télécharger
        </a>
      </Button>
    </div>
  );
}

function VideoPreview({ item, videos, initial }: { item: MarketingItemView; videos: MarketingFileView[]; initial: string }) {
  const [tab, setTab] = useState(videos.some((video) => video.key === initial) ? initial : videos[0].key);
  const video = videos.find((candidate) => candidate.key === tab) ?? videos[0];
  return (
    <div className="grid min-h-0 gap-3">
      {videos.length > 1 && (
        <Tabs onValueChange={setTab} value={tab}>
          <TabsList>
            {videos.map((candidate) => (
              <TabsTrigger className="px-3 text-[13px]" key={candidate.key} value={candidate.key}>
                {candidate.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      )}
      {/* Pas de lecture automatique ; le serveur répond aux plages (Safari, iOS). */}
      <video
        className="max-h-[70dvh] w-full rounded-md bg-foreground object-contain max-sm:max-h-[60dvh]"
        controls
        key={video.key}
        playsInline
        poster={item.thumbnail?.url}
        preload="metadata"
        src={video.url}
      />
      <FileFooter file={video} />
    </div>
  );
}

function ImagePreview({ file }: { file: MarketingFileView }) {
  return (
    <div className="grid min-h-0 gap-3">
      <div className={cn("flex justify-center rounded-md border p-4", file.background === "dark" ? "bg-foreground" : "bg-muted")}>
        <img alt={file.label} className="max-h-[66dvh] w-auto max-w-full object-contain" src={file.url} />
      </div>
      <FileFooter file={file} />
    </div>
  );
}

function SlidesPreview({ slides, document }: { slides: MarketingFileView[]; document?: MarketingFileView }) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(1);

  useEffect(() => {
    if (!api) return;
    const update = () => setCurrent(api.selectedScrollSnap() + 1);
    api.on("select", update);
    return () => {
      api.off("select", update);
    };
  }, [api]);

  return (
    <div className="grid min-h-0 gap-3">
      <Carousel setApi={setApi}>
        <CarouselContent>
          {slides.map((slide) => (
            <CarouselItem key={slide.key}>
              <div className="flex justify-center rounded-md bg-muted p-3">
                <img alt={slide.label} className="max-h-[62dvh] w-auto max-w-full object-contain" src={slide.url} />
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <div className="mt-3 flex items-center justify-center gap-3">
          <CarouselPrevious className="static size-9 translate-y-0 rounded-md max-sm:size-11" />
          <span aria-live="polite" className="min-w-14 text-center text-[13px] text-muted-foreground tabular-nums">
            {current} / {slides.length}
          </span>
          <CarouselNext className="static size-9 translate-y-0 rounded-md max-sm:size-11" />
        </div>
      </Carousel>
      {document && <FileFooter file={document} />}
    </div>
  );
}

/**
 * Aperçu d'un contenu : la vidéo (onglets entre ses formats), une image, ou
 * les pages d'un carrousel. `selected` est la clé du fichier ouvert, null
 * quand le dialogue est fermé.
 */
export function PreviewDialog({
  item,
  selected,
  onClose,
}: {
  item: MarketingItemView;
  selected: string | null;
  onClose: () => void;
}) {
  const videos = item.variants.filter((file) => file.kind === "video" && !file.missing);
  const images = item.variants.filter((file) => file.kind === "image" && !file.missing);
  const slides = item.slides.filter((file) => !file.missing);
  const image = images.find((file) => file.key === selected);

  let content: React.ReactNode = null;
  if (selected !== null) {
    if (image) content = <ImagePreview file={image} />;
    else if (selected.startsWith("slide-") && slides.length) {
      content = <SlidesPreview document={item.variants.find((file) => file.kind === "pdf" && !file.missing)} slides={slides} />;
    } else if (videos.length) content = <VideoPreview initial={selected} item={item} key={selected} videos={videos} />;
    else if (images.length) content = <ImagePreview file={images[0]} />;
  }

  return (
    <Dialog onOpenChange={(open) => !open && onClose()} open={selected !== null && content !== null}>
      <DialogContent className="flex max-h-[calc(100dvh-2rem)] flex-col gap-4 overflow-y-auto sm:max-w-[min(960px,calc(100%-4rem))] max-sm:h-dvh max-sm:max-h-dvh max-sm:max-w-full max-sm:rounded-none max-sm:border-0 max-sm:p-4">
        <DialogHeader className="pr-8 text-left">
          <DialogTitle className="text-[15px] leading-snug">{item.title}</DialogTitle>
          <DialogDescription className="text-[12.5px]">{item.summary}</DialogDescription>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );
}
